import React, { useEffect, useRef, useState } from 'react';
import { X, ScanBarcode, Loader2, AlertTriangle } from 'lucide-react';
import { Button } from './ui/button';
import { useT } from '../services/i18n';

const FORMATS = ['ean_13', 'ean_8', 'upc_a', 'upc_e', 'code_128'];
const clean = (v) => String(v || '').replace(/\D/g, '');

// Camera barcode scanner (native BarcodeDetector, ZXing fallback) → EAN of a part
const EanScanner = ({ open, onClose, onDetect }) => {
  const { t } = useT();
  const videoRef = useRef(null);
  const [status, setStatus] = useState('starting');
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return undefined;
    let stop = () => {};
    let cancelled = false;
    setStatus('starting'); setError('');
    const finish = (code) => { if (cancelled) return; cancelled = true; stop(); onDetect(code); };
    (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: { ideal: 'environment' }, width: { ideal: 1280 }, height: { ideal: 720 } }, audio: false });
        if (cancelled) { stream.getTracks().forEach(tr => tr.stop()); return; }
        const video = videoRef.current;
        video.srcObject = stream;
        await video.play();
        setStatus('scanning');
        if ('BarcodeDetector' in window) {
          const detector = new window.BarcodeDetector({ formats: FORMATS });
          const tick = async () => {
            if (cancelled) return;
            try {
              const codes = await detector.detect(video);
              const hit = codes.map(c => clean(c.rawValue)).find(v => v.length >= 8);
              if (hit) return finish(hit);
            } catch { /* frame not ready */ }
            setTimeout(tick, 150);
          };
          stop = () => stream.getTracks().forEach(tr => tr.stop());
          tick();
        } else {
          const { BrowserMultiFormatReader } = await import('@zxing/browser');
          const reader = new BrowserMultiFormatReader();
          const controls = await reader.decodeFromStream(stream, video, (result) => {
            const v = result && clean(result.getText());
            if (v && v.length >= 8) finish(v);
          });
          stop = () => { controls.stop(); stream.getTracks().forEach(tr => tr.stop()); };
        }
      } catch (e) {
        if (cancelled) return;
        setStatus('error');
        setError(e?.name === 'NotAllowedError' ? t('scan.denied') : t('scan.unavailable'));
      }
    })();
    return () => { cancelled = true; stop(); };
  }, [open, onDetect, t]);

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[100] bg-black/90 flex flex-col text-white" data-testid="ean-scanner" role="dialog" aria-modal="true">
      <div className="flex items-center justify-between px-4 py-3">
        <p className="font-semibold inline-flex items-center gap-2"><ScanBarcode size={18} className="text-orange-400" /> {t('scan.title')}</p>
        <button type="button" onClick={onClose} className="p-2 rounded-full hover:bg-white/10" aria-label={t('common.close')} data-testid="ean-scanner-close"><X size={22} /></button>
      </div>
      <div className="relative flex-1 flex items-center justify-center overflow-hidden">
        <video ref={videoRef} className="w-full h-full object-cover" playsInline muted data-testid="ean-scanner-video" />
        {status === 'scanning' && (
          <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
            <div className="w-[78%] max-w-md aspect-[2/1] border-2 border-orange-400 rounded-xl shadow-[0_0_0_9999px_rgba(0,0,0,0.45)] relative overflow-hidden">
              <div className="absolute left-0 right-0 h-0.5 bg-orange-400/90 animate-[scanline_2s_ease-in-out_infinite]" />
            </div>
          </div>
        )}
        {status === 'starting' && <div className="absolute inset-0 flex items-center justify-center"><Loader2 size={32} className="animate-spin text-orange-400" /></div>}
        {status === 'error' && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 px-6 text-center" data-testid="ean-scanner-error">
            <AlertTriangle size={32} className="text-orange-400" />
            <p className="text-sm">{error}</p>
            <Button variant="outline" onClick={onClose} className="text-gray-900">{t('common.close')}</Button>
          </div>
        )}
      </div>
      <p className="px-4 py-3 text-center text-xs text-gray-300" data-testid="ean-scanner-hint">{t('scan.hint')}</p>
      <style>{'@keyframes scanline{0%{top:4%}50%{top:92%}100%{top:4%}}'}</style>
    </div>
  );
};

export default EanScanner;
